import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL, { BASE_URL } from '../../../config';
import './BrandsTab.css';

const BrandsTab = ({ showMessage }) => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBrand, setEditingBrand] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        logo_dark: null,
        logo_light: null,
        gif: null,
        reference_url: '',
        ad_text: '',
        description: '',
        default_wm_position: 'bottom-right',
        default_wm_size: 100,
        default_wm_opacity: 80
    });

    const [previewUrls, setPreviewUrls] = useState({
        dark: null,
        light: null,
        gif: null
    });
    const [submitting, setSubmitting] = useState(false);

    // Markaları yükle
    const fetchBrands = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_URL}/brands`);
            setBrands(response.data.brands || []);
        } catch (error) {
            console.error('Brands fetch error:', error);
            showMessage('Markalar yüklenemedi', 'error');
        } finally {
            setLoading(false);
        }
    }, [showMessage]);

    useEffect(() => {
        fetchBrands();
    }, [fetchBrands]);

    // Form reset
    const resetForm = () => {
        setFormData({
            name: '',
            logo_dark: null,
            logo_light: null,
            gif: null,
            reference_url: '',
            ad_text: '',
            description: '',
            default_wm_position: 'bottom-right',
            default_wm_size: 100,
            default_wm_opacity: 80
        });
        setPreviewUrls({ dark: null, light: null, gif: null });
        setEditingBrand(null);
    };

    // Modal aç
    const openModal = (brand = null) => {
        if (brand) {
            setEditingBrand(brand);
            setFormData({
                name: brand.name,
                logo_dark: null,
                logo_light: null,
                gif: null,
                reference_url: brand.reference_url || '',
                ad_text: brand.ad_text || '',
                description: brand.description || '',
                default_wm_position: brand.default_wm_position || 'bottom-right',
                default_wm_size: brand.default_wm_size || 100,
                default_wm_opacity: brand.default_wm_opacity || 80
            });
            setPreviewUrls({
                dark: brand.logo_dark_url ? `${BASE_URL}${brand.logo_dark_url}` : null,
                light: brand.logo_light_url ? `${BASE_URL}${brand.logo_light_url}` : null,
                gif: brand.gif_url ? `${BASE_URL}${brand.gif_url}` : null
            });
        } else {
            resetForm();
        }
        setIsModalOpen(true);
    };

    // Modal kapat
    const closeModal = () => {
        setIsModalOpen(false);
        resetForm();
    };

    // Logo seç
    const handleLogoChange = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            if (type === 'gif') {
                setFormData(prev => ({ ...prev, gif: file }));
            } else {
                setFormData(prev => ({ ...prev, [`logo_${type}`]: file }));
            }
            setPreviewUrls(prev => ({ ...prev, [type]: URL.createObjectURL(file) }));
        }
    };

    // Logo kaldır
    const removeLogo = (type) => {
        if (type === 'gif') {
            setFormData(prev => ({ ...prev, gif: null, remove_gif: true }));
        } else {
            setFormData(prev => ({ ...prev, [`logo_${type}`]: null, [`remove_logo_${type}`]: true }));
        }
        setPreviewUrls(prev => ({ ...prev, [type]: null }));
    };
    // Form gönder
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            showMessage('Marka adı gerekli', 'error');
            return;
        }

        setSubmitting(true);

        try {
            const submitData = new FormData();
            submitData.append('name', formData.name.trim());
            submitData.append('reference_url', formData.reference_url || '');
            submitData.append('ad_text', formData.ad_text || '');
            submitData.append('description', formData.description || '');

            if (formData.logo_dark) {
                submitData.append('logo_dark', formData.logo_dark);
            }

            if (formData.logo_light) {
                submitData.append('logo_light', formData.logo_light);
            }

            if (formData.gif) {
                submitData.append('gif', formData.gif);
            }

            // Logo kaldırma bayrakları arasına:
            if (formData.remove_gif) {
                submitData.append('remove_gif', 'true');
            }

            // Logo kaldırma bayrakları
            if (formData.remove_logo_dark) {
                submitData.append('remove_logo_dark', 'true');
            }

            if (formData.remove_logo_light) {
                submitData.append('remove_logo_light', 'true');
            }

            submitData.append('default_wm_position', formData.default_wm_position);
            submitData.append('default_wm_size', formData.default_wm_size);
            submitData.append('default_wm_opacity', formData.default_wm_opacity);

            if (editingBrand) {
                await axios.put(`${API_URL}/brands/${editingBrand.id}`, submitData);
                showMessage('Marka güncellendi', 'success');
            } else {
                await axios.post(`${API_URL}/brands`, submitData);
                showMessage('Marka eklendi', 'success');
            }

            closeModal();
            fetchBrands();
        } catch (error) {
            console.error('Brand save error:', error);
            showMessage(error.response?.data?.error || 'İşlem başarısız', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    // Marka sil
    const handleDelete = async (brand) => {
        if (!window.confirm(`"${brand.name}" markasını silmek istediğinize emin misiniz?`)) {
            return;
        }

        try {
            await axios.delete(`${API_URL}/brands/${brand.id}`);
            showMessage('Marka silindi', 'success');
            fetchBrands();
        } catch (error) {
            console.error('Brand delete error:', error);
            showMessage('Marka silinemedi', 'error');
        }
    };

    // Aktif/Pasif toggle
    const toggleActive = async (brand) => {
        try {
            const submitData = new FormData();
            submitData.append('is_active', !brand.is_active);

            await axios.put(`${API_URL}/brands/${brand.id}`, submitData);
            showMessage(brand.is_active ? 'Marka pasif yapıldı' : 'Marka aktif yapıldı', 'success');
            fetchBrands();
        } catch (error) {
            console.error('Toggle active error:', error);
            showMessage('İşlem başarısız', 'error');
        }
    };

    if (loading) {
        return (
            <div className="brands-tab">
                <div className="brands-loading">
                    <div className="loading-spinner"></div>
                    <p>Markalar yükleniyor...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="brands-tab">
            <div className="brands-header">
                <button className="brands-add-btn" onClick={() => openModal()}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14" />
                    </svg>
                    Yeni Marka
                </button>
            </div>

            {brands.length === 0 ? (
                <div className="brands-empty">
                    <div className="empty-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                        </svg>
                    </div>
                    <h3>Henüz marka eklenmemiş</h3>
                    <p>İçeriklerinize watermark olarak eklemek için marka logoları ekleyin</p>
                    <button className="empty-add-btn" onClick={() => openModal()}>
                        Marka Ekle
                    </button>
                </div>
            ) : (
                <div className="brands-grid">
                    {brands.map(brand => (
                        <div key={brand.id} className={`brand-card ${!brand.is_active ? 'inactive' : ''}`}>
                            <div className="brand-logos">
                                <div className="brand-logo dark">
                                    {brand.logo_dark_url ? (
                                        <img src={`${BASE_URL}${brand.logo_dark_url}`} alt={`${brand.name} - Koyu`} />
                                    ) : (
                                        <div className="brand-logo-placeholder dark">
                                            <span>{brand.name.charAt(0).toUpperCase()}</span>
                                        </div>
                                    )}
                                    <span className="logo-label">Koyu</span>
                                </div>
                                <div className="brand-logo light">
                                    {brand.logo_light_url ? (
                                        <img src={`${BASE_URL}${brand.logo_light_url}`} alt={`${brand.name} - Açık`} />
                                    ) : (
                                        <div className="brand-logo-placeholder light">
                                            <span>{brand.name.charAt(0).toUpperCase()}</span>
                                        </div>
                                    )}
                                    <span className="logo-label">Açık</span>
                                </div>
                                {brand.gif_url && (
                                    <div className="brand-logo gif">
                                        <img src={`${BASE_URL}${brand.gif_url}`} alt={`${brand.name} - GIF`} />
                                        <span className="logo-label">GIF</span>
                                    </div>
                                )}
                            </div>
                            <div className="brand-info">
                                <h3>{brand.name}</h3>
                                {brand.reference_url && (
                                    <a href={brand.reference_url} target="_blank" rel="noopener noreferrer" className="brand-link">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                            <polyline points="15 3 21 3 21 9" />
                                            <line x1="10" y1="14" x2="21" y2="3" />
                                        </svg>
                                        Referans Link
                                    </a>
                                )}
                                {brand.ad_text && <p className="brand-ad-text">{brand.ad_text}</p>}
                            </div>
                            <div className="brand-actions">
                                <button
                                    className={`brand-toggle ${brand.is_active ? 'active' : ''}`}
                                    onClick={() => toggleActive(brand)}
                                    title={brand.is_active ? 'Pasif yap' : 'Aktif yap'}
                                >
                                    {brand.is_active ? (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    )}
                                </button>
                                <button
                                    className="brand-edit"
                                    onClick={() => openModal(brand)}
                                    title="Düzenle"
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                </button>
                                <button
                                    className="brand-delete"
                                    onClick={() => handleDelete(brand)}
                                    title="Sil"
                                >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="brands-modal-overlay" onClick={closeModal}>
                    <div className="brands-modal" onClick={e => e.stopPropagation()}>
                        <div className="brands-modal-header">
                            <h3>{editingBrand ? 'Marka Düzenle' : 'Yeni Marka'}</h3>
                            <button className="brands-modal-close" onClick={closeModal}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="brands-modal-form">
                            {/* Marka Adı */}
                            <div className="brands-form-group">
                                <label>Marka Adı *</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Örn: Şirket Adı"
                                    required
                                />
                            </div>

                            {/* Logo Koyu */}
                            <div className="brands-form-group">
                                <label>
                                    Logo (Koyu Arka Plan İçin)
                                    <span className="label-hint">Açık renkli logo - koyu arka planlarda kullanılır</span>
                                </label>
                                <div className="brands-logo-upload">
                                    {previewUrls.dark ? (
                                        <div className="brands-logo-preview dark">
                                            <img src={previewUrls.dark} alt="Dark Preview" />
                                            <button
                                                type="button"
                                                className="brands-logo-remove"
                                                onClick={() => removeLogo('dark')}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18" />
                                                    <line x1="6" y1="6" x2="18" y2="18" />
                                                </svg>
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="brands-logo-dropzone dark">
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                                                onChange={(e) => handleLogoChange(e, 'dark')}
                                            />
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="17 8 12 3 7 8" />
                                                <line x1="12" y1="3" x2="12" y2="15" />
                                            </svg>
                                            <span>Koyu arka plan logosu yükle</span>
                                            <small>PNG, JPEG, WebP, SVG - Max 5MB</small>
                                        </label>
                                    )}
                                </div>
                            </div>

                            {/* Logo Açık */}
                            <div className="brands-form-group">
                                <label>
                                    Logo (Açık Arka Plan İçin)
                                    <span className="label-hint">Koyu renkli logo - açık arka planlarda kullanılır</span>
                                </label>
                                <div className="brands-logo-upload">
                                    {previewUrls.light ? (
                                        <div className="brands-logo-preview light">
                                            <img src={previewUrls.light} alt="Light Preview" />
                                            <button
                                                type="button"
                                                className="brands-logo-remove"
                                                onClick={() => removeLogo('light')}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18" />
                                                    <line x1="6" y1="6" x2="18" y2="18" />
                                                </svg>
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="brands-logo-dropzone light">
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                                                onChange={(e) => handleLogoChange(e, 'light')}
                                            />
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="17 8 12 3 7 8" />
                                                <line x1="12" y1="3" x2="12" y2="15" />
                                            </svg>
                                            <span>Açık arka plan logosu yükle</span>
                                            <small>PNG, JPEG, WebP, SVG - Max 5MB</small>
                                        </label>
                                    )}
                                </div>
                            </div>

                            {/* GIF Watermark */}
                            <div className="brands-form-group">
                                <label>
                                    GIF Watermark
                                    <span className="label-hint">Animasyonlu GIF - videolarda dönen watermark olarak kullanılır</span>
                                </label>
                                <div className="brands-logo-upload">
                                    {previewUrls.gif ? (
                                        <div className="brands-logo-preview gif">
                                            <img src={previewUrls.gif} alt="GIF Preview" />
                                            <button
                                                type="button"
                                                className="brands-logo-remove"
                                                onClick={() => removeLogo('gif')}
                                            >
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <line x1="18" y1="6" x2="6" y2="18" />
                                                    <line x1="6" y1="6" x2="18" y2="18" />
                                                </svg>
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="brands-logo-dropzone gif">
                                            <input
                                                type="file"
                                                accept="image/gif"
                                                onChange={(e) => handleLogoChange(e, 'gif')}
                                            />
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                <polyline points="17 8 12 3 7 8" />
                                                <line x1="12" y1="3" x2="12" y2="15" />
                                            </svg>
                                            <span>GIF watermark yükle</span>
                                            <small>GIF - Max 10MB</small>
                                        </label>
                                    )}
                                </div>
                            </div>

                            {/* Referans Linki */}
                            <div className="brands-form-group">
                                <label>Referans Linki</label>
                                <input
                                    type="url"
                                    value={formData.reference_url}
                                    onChange={e => setFormData(prev => ({ ...prev, reference_url: e.target.value }))}
                                    placeholder="https://example.com"
                                />
                            </div>

                            {/* Reklam Metni */}
                            <div className="brands-form-group">
                                <label>Reklam Metni</label>
                                <textarea
                                    value={formData.ad_text}
                                    onChange={e => setFormData(prev => ({ ...prev, ad_text: e.target.value }))}
                                    placeholder="Bu metin içeriklerde reklam olarak kullanılabilir..."
                                    rows={3}
                                />
                            </div>

                            {/* Varsayılan Watermark Ayarları */}
                            <div className="brands-form-group">
                                <label>
                                    Varsayılan Watermark Ayarları
                                    <span className="label-hint">Watermark eklerken bu marka seçildiğinde otomatik uygulanır</span>
                                </label>

                                <div className="brands-wm-settings">
                                    {/* Pozisyon */}
                                    <div className="brands-wm-row">
                                        <span className="brands-wm-label">Pozisyon</span>
                                        <select
                                            value={formData.default_wm_position}
                                            onChange={e => setFormData(prev => ({ ...prev, default_wm_position: e.target.value }))}
                                            className="brands-wm-select"
                                        >
                                            <option value="top-left">Sol Üst</option>
                                            <option value="top-center">Orta Üst</option>
                                            <option value="top-right">Sağ Üst</option>
                                            <option value="center-left">Sol Orta</option>
                                            <option value="center">Orta</option>
                                            <option value="center-right">Sağ Orta</option>
                                            <option value="bottom-left">Sol Alt</option>
                                            <option value="bottom-center">Orta Alt</option>
                                            <option value="bottom-right">Sağ Alt</option>
                                        </select>
                                    </div>

                                    {/* Boyut */}
                                    <div className="brands-wm-row">
                                        <span className="brands-wm-label">Boyut</span>
                                        <div className="brands-wm-slider-group">
                                            <input
                                                type="range"
                                                min="30"
                                                max="200"
                                                value={formData.default_wm_size}
                                                onChange={e => setFormData(prev => ({ ...prev, default_wm_size: Number(e.target.value) }))}
                                                className="brands-wm-slider"
                                            />
                                            <span className="brands-wm-value">{formData.default_wm_size}%</span>
                                        </div>
                                    </div>

                                    {/* Opaklık */}
                                    <div className="brands-wm-row">
                                        <span className="brands-wm-label">Opaklık</span>
                                        <div className="brands-wm-slider-group">
                                            <input
                                                type="range"
                                                min="10"
                                                max="100"
                                                value={formData.default_wm_opacity}
                                                onChange={e => setFormData(prev => ({ ...prev, default_wm_opacity: Number(e.target.value) }))}
                                                className="brands-wm-slider"
                                            />
                                            <span className="brands-wm-value">{formData.default_wm_opacity}%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Açıklama */}
                            <div className="brands-form-group">
                                <label>Açıklama (Dahili Not)</label>
                                <textarea
                                    value={formData.description}
                                    onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                    placeholder="Sadece sizin göreceğiniz dahili not..."
                                    rows={2}
                                />
                            </div>

                            <div className="brands-modal-actions">
                                <button type="button" className="brands-btn-cancel" onClick={closeModal}>
                                    İptal
                                </button>
                                <button type="submit" className="brands-btn-submit" disabled={submitting}>
                                    {submitting ? 'Kaydediliyor...' : (editingBrand ? 'Güncelle' : 'Ekle')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BrandsTab;